import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileSignature,
  Plus,
  Search,
  Download,
  Eye,
  Pencil,
  Trash2,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Building2,
  Handshake,
  Users,
  FileText,
  Copy,
  Sparkles,
  ShieldAlert,
  Briefcase,
  Layers,
  Receipt,
  Scale,
  DollarSign,
  Printer,
} from "lucide-react";
import { toast } from "sonner";
import {
  listContratos,
  listContratoModelos,
  upsertContrato,
  upsertContratoModelo,
  deleteContrato,
  preencherVariaveisContrato,
  VARIAVEIS_CONTRATO,
  type Contrato,
  type ContratoModelo,
} from "@/lib/contratos.functions";
import {
  listContratosRepresentacao,
  deleteContratoRepresentacao,
} from "@/lib/representacao-contratos.functions";
import {
  ContratoRepresentacao,
  STATUS_CONTRATO_LABELS,
  STATUS_CONTRATO_COLORS,
} from "@/types/representacao-contratos.types";
import { ContratoRepresentacaoFormModal } from "@/components/contratos/ContratoRepresentacaoFormModal";
import { PainelLiquidacaoRepasses } from "@/components/contratos/PainelLiquidacaoRepasses";
import { SimuladorLiquidacaoWidget } from "@/components/contratos/SimuladorLiquidacaoWidget";
import { fmtBRL } from "@/lib/representacao-contratos";
import { gerarPdfContrato } from "@/lib/contratos-pdf";
import { useTenantBranding } from "@/hooks/use-tenant-branding";
import { listClientes } from "@/lib/clientes.functions";
import { listAgencias } from "@/lib/agencias.functions";
import { listParceiros } from "@/lib/parceiros.functions";
import { listPis } from "@/lib/pi.functions";
import {
  UniversalAssinaturaModal,
  DocumentoAssinaturaBadge,
} from "@/components/UniversalAssinaturaModal";

export const Route = createFileRoute("/contratos")({
  head: () => ({ meta: [{ title: "Contratos & Modelos — Mídia.OS" }] }),
  component: ContratosPage,
});

function formatBRL(v: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
}

export function ContratosPage() {
  const qc = useQueryClient();
  const { nome: empresaNome, logoSrc } = useTenantBranding();

  const listContratosFn = useServerFn(listContratos);
  const listModelosFn = useServerFn(listContratoModelos);
  const upsertContratoFn = useServerFn(upsertContrato);
  const upsertModeloFn = useServerFn(upsertContratoModelo);
  const deleteContratoFn = useServerFn(deleteContrato);

  const listContratosRepresentacaoFn = useServerFn(listContratosRepresentacao);
  const deleteContratoRepresentacaoFn = useServerFn(deleteContratoRepresentacao);

  const listClientesFn = useServerFn(listClientes);
  const listAgenciasFn = useServerFn(listAgencias);
  const listParceirosFn = useServerFn(listParceiros);
  const listPisFn = useServerFn(listPis);

  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"representacao" | "liquidacao" | "contratos" | "modelos">("representacao");
  const [statusFiltro, setStatusFiltro] = useState<string>("todos");

  // Filtros de Contratos de Representação
  const [searchRepresentacao, setSearchRepresentacao] = useState("");
  const [statusFiltroRepresentacao, setStatusFiltroRepresentacao] = useState<string>("todos");

  // Modais de Contrato de Representação
  const [modalRepresentacaoOpen, setModalRepresentacaoOpen] = useState(false);
  const [editingContratoRepresentacao, setEditingContratoRepresentacao] = useState<ContratoRepresentacao | null>(null);
  const [viewMinutaRepresentacao, setViewMinutaRepresentacao] = useState<ContratoRepresentacao | null>(null);

  // Modais de Contrato Geral
  const [modalContratoOpen, setModalContratoOpen] = useState(false);
  const [viewContrato, setViewContrato] = useState<Contrato | null>(null);
  const [assinaturaContrato, setAssinaturaContrato] = useState<Contrato | null>(null);

  // Form State Contrato
  const [formData, setFormData] = useState<{
    id?: string;
    numero?: string;
    tipo: string;
    titulo: string;
    modelo_id?: string;
    cliente_id?: string;
    agencia_id?: string;
    parceiro_id?: string;
    pi_id?: string;
    valor: number;
    comissao_pct: number;
    comissao_valor: number;
    repasse_valor: number;
    data_inicio: string;
    data_fim: string;
    status: Contrato["status"];
    conteudo_gerado: string;
    observacoes: string;
  }>({
    tipo: "cliente",
    titulo: "",
    valor: 0,
    comissao_pct: 0,
    comissao_valor: 0,
    repasse_valor: 0,
    data_inicio: "",
    data_fim: "",
    status: "rascunho",
    conteudo_gerado: "",
    observacoes: "",
  });

  // Modal de Modelo
  const [modalModeloOpen, setModalModeloOpen] = useState(false);
  const [modeloForm, setModeloForm] = useState<{
    id?: string;
    titulo: string;
    tipo: ContratoModelo["tipo"];
    conteudo: string;
  }>({
    titulo: "",
    tipo: "cliente",
    conteudo: "",
  });

  // Queries
  const { data: contratos = [], isLoading: loadingContratos } = useQuery({
    queryKey: ["contratos"],
    queryFn: () => listContratosFn(),
  });

  const { data: modelos = [], isLoading: loadingModelos } = useQuery({
    queryKey: ["contrato-modelos"],
    queryFn: () => listModelosFn(),
  });

  const { data: clientes = [] } = useQuery({
    queryKey: ["clientes-select"],
    queryFn: () => listClientesFn(),
  });

  const { data: agencias = [] } = useQuery({
    queryKey: ["agencias-select"],
    queryFn: () => listAgenciasFn(),
  });

  const { data: parceiros = [] } = useQuery({
    queryKey: ["parceiros-select"],
    queryFn: () => listParceirosFn(),
  });

  const { data: pis = [] } = useQuery({
    queryKey: ["pis-select"],
    queryFn: () => listPisFn(),
  });

  // Query Contratos de Representação (Tenant Nexo Mídia)
  const { data: contratosRepresentacao = [], isLoading: loadingRepresentacao } = useQuery({
    queryKey: ["contratos_representacao", "68.279.031/0001-67"],
    queryFn: () => listContratosRepresentacaoFn({ data: { tenantCnpj: "68.279.031/0001-67" } }),
  });

  // Filtros de Contratos de Representação
  const contratosRepresentacaoFiltrados = useMemo(() => {
    return contratosRepresentacao.filter((c) => {
      const matchSearch =
        searchRepresentacao === "" ||
        c.numero_contrato.toLowerCase().includes(searchRepresentacao.toLowerCase()) ||
        (c.parceiro?.nome && c.parceiro.nome.toLowerCase().includes(searchRepresentacao.toLowerCase())) ||
        (c.parceiro?.razao_social && c.parceiro.razao_social.toLowerCase().includes(searchRepresentacao.toLowerCase())) ||
        (c.territorio && c.territorio.toLowerCase().includes(searchRepresentacao.toLowerCase()));

      const matchStatus =
        statusFiltroRepresentacao === "todos" || c.status === statusFiltroRepresentacao;

      return matchSearch && matchStatus;
    });
  }, [contratosRepresentacao, searchRepresentacao, statusFiltroRepresentacao]);

  // Filtros
  const contratosFiltrados = useMemo(() => {
    return contratos.filter((c) => {
      const matchSearch =
        c.numero.toLowerCase().includes(search.toLowerCase()) ||
        c.titulo.toLowerCase().includes(search.toLowerCase()) ||
        c.cliente?.razao_social?.toLowerCase().includes(search.toLowerCase()) ||
        c.parceiro?.razao_social?.toLowerCase().includes(search.toLowerCase());

      const matchStatus = statusFiltro === "todos" || c.status === statusFiltro;
      return matchSearch && matchStatus;
    });
  }, [contratos, search, statusFiltro]);

  // Handler para pré-preenchimento ao selecionar Modelo
  function handleSelectModelo(modeloId: string) {
    const mod = modelos.find((m) => m.id === modeloId);
    if (!mod) return;

    const cli = clientes.find((c: any) => c.id === formData.cliente_id);
    const age = agencias.find((a: any) => a.id === formData.agencia_id);
    const parc = parceiros.find((p: any) => p.id === formData.parceiro_id);
    const pi = pis.find((p: any) => p.id === formData.pi_id);

    const dadosSubstituicao: Record<string, string | number> = {
      NOME_EMPRESA: empresaNome || "Mídia OS",
      CNPJ_EMPRESA: "Consulte Dados Cadastrais",
      CLIENTE: cli ? (cli as any).razao_social || (cli as any).nome_fantasia : "Cliente",
      CNPJ_CLIENTE: cli ? (cli as any).cnpj || "—" : "—",
      AGENCIA: age ? (age as any).razao_social || (age as any).nome_fantasia : "Venda Direta",
      CNPJ_AGENCIA: age ? (age as any).cnpj || "—" : "—",
      PARCEIRO: parc ? (parc as any).razao_social || (parc as any).nome_fantasia : "Veículo",
      CNPJ_PARCEIRO: parc ? (parc as any).cnpj || "—" : "—",
      CAMPANHA: pi ? (pi as any).campanha : "Campanha de Mídia",
      VALOR: formatBRL(formData.valor),
      COMISSAO: `${formData.comissao_pct}% (${formatBRL(formData.comissao_valor)})`,
      REPASSE: formatBRL(formData.repasse_valor),
      DATA_INICIO: formData.data_inicio || new Date().toLocaleDateString("pt-BR"),
      DATA_FIM: formData.data_fim || "A definir",
      RESPONSAVEL: "Diretoria Comercial",
    };

    const textoGerado = preencherVariaveisContrato(mod.conteudo, dadosSubstituicao);
    setFormData((f) => ({
      ...f,
      modelo_id: modeloId,
      titulo: f.titulo || mod.titulo,
      conteudo_gerado: textoGerado,
    }));
  }

  // Mutações
  const saveContratoMut = useMutation({
    mutationFn: (data: any) => upsertContratoFn({ data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contratos"] });
      toast.success("Contrato salvo com sucesso!");
      setModalContratoOpen(false);
    },
    onError: (err: any) => toast.error(err.message || "Erro ao salvar contrato"),
  });

  const saveModeloMut = useMutation({
    mutationFn: (data: any) => upsertModeloFn({ data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contrato-modelos"] });
      toast.success("Modelo de contrato salvo com sucesso!");
      setModalModeloOpen(false);
    },
    onError: (err: any) => toast.error(err.message || "Erro ao salvar modelo"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteContratoFn({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contratos"] });
      toast.success("Contrato removido!");
    },
  });

  const deleteRepresentacaoMut = useMutation({
    mutationFn: (id: string) => deleteContratoRepresentacaoFn({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contratos_representacao"] });
      toast.success("Contrato de representação removido!");
    },
    onError: (err: any) => toast.error(err.message || "Erro ao excluir"),
  });

  return (
    <AppShell>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Cabeçalho */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <FileSignature className="size-6 text-primary" />
              <h1 className="text-2xl font-bold tracking-tight">Contratos & Instrumentos Jurídicos</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              Gestão de contratos de representação comercial, veículos, liquidação financeira e minutas jurídicas integradas.
            </p>
          </div>

          <div className="flex items-center gap-2">
            {tab === "representacao" ? (
              <Button
                onClick={() => {
                  setEditingContratoRepresentacao(null);
                  setModalRepresentacaoOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="size-4" />
                Novo Contrato de Representação
              </Button>
            ) : tab === "contratos" ? (
              <Button
                onClick={() => {
                  setFormData({
                    tipo: "cliente",
                    titulo: "",
                    valor: 0,
                    comissao_pct: 0,
                    comissao_valor: 0,
                    repasse_valor: 0,
                    data_inicio: new Date().toISOString().split("T")[0],
                    data_fim: new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0],
                    status: "rascunho",
                    conteudo_gerado: "",
                    observacoes: "",
                  });
                  setModalContratoOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="size-4" />
                Novo Contrato
              </Button>
            ) : tab === "modelos" ? (
              <Button
                onClick={() => {
                  setModeloForm({ titulo: "", tipo: "cliente", conteudo: "" });
                  setModalModeloOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="size-4" />
                Novo Modelo
              </Button>
            ) : null}
          </div>
        </div>

        {/* Alerta de Revisão Jurídica */}
        <div className="p-3.5 rounded-lg border border-amber-500/30 bg-amber-500/10 flex items-start gap-3">
          <ShieldAlert className="size-5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-900 dark:text-amber-200">
            <strong>Aviso de Conformidade Jurídica:</strong> Os contratos de representação comercial e minutas disponibilizados
            possuem cláusulas automáticas de blindagem (pós-rescisão, renovações e split fiscal). Recomendamos validação jurídica final.
          </p>
        </div>

        {/* Abas */}
        <Tabs value={tab} onValueChange={(v: any) => setTab(v)}>
          <TabsList className="grid w-full max-w-3xl grid-cols-2 md:grid-cols-4">
            <TabsTrigger value="representacao" className="text-xs">
              Representação de Veículos ({contratosRepresentacao.length})
            </TabsTrigger>
            <TabsTrigger value="liquidacao" className="text-xs">
              Liquidação & Repasses
            </TabsTrigger>
            <TabsTrigger value="contratos" className="text-xs">
              Contratos Gerais ({contratos.length})
            </TabsTrigger>
            <TabsTrigger value="modelos" className="text-xs">
              Modelos ({modelos.length})
            </TabsTrigger>
          </TabsList>

          {/* ABA 1: CONTRATOS DE REPRESENTAÇÃO (NEXO MÍDIA) */}
          <TabsContent value="representacao" className="space-y-4 pt-2">
            {/* Filtros e Busca */}
            <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
              <div className="relative w-full sm:w-80">
                <Search className="size-4 absolute left-3 top-3 text-muted-foreground" />
                <Input
                  placeholder="Buscar contrato, parceiro ou território..."
                  className="pl-9 text-xs"
                  value={searchRepresentacao}
                  onChange={(e) => setSearchRepresentacao(e.target.value)}
                />
              </div>

              <div className="flex gap-2 w-full sm:w-auto">
                <Select
                  value={statusFiltroRepresentacao}
                  onValueChange={setStatusFiltroRepresentacao}
                >
                  <SelectTrigger className="w-44 text-xs">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os Status</SelectItem>
                    <SelectItem value="rascunho">Rascunho</SelectItem>
                    <SelectItem value="enviado_assinatura">Enviado p/ Assinatura</SelectItem>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="suspenso">Suspenso</SelectItem>
                    <SelectItem value="rescindido">Rescindido</SelectItem>
                    <SelectItem value="vencido">Vencido</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tabela de Contratos de Representação */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Número / Veículo Parceiro</TableHead>
                      <TableHead>Território & Produtos</TableHead>
                      <TableHead>Faturamento Habilitado</TableHead>
                      <TableHead>Remuneração / Split</TableHead>
                      <TableHead>Blindagem Jurídica</TableHead>
                      <TableHead>Vigência</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingRepresentacao ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          Carregando contratos de representação comercial...
                        </TableCell>
                      </TableRow>
                    ) : contratosRepresentacaoFiltrados.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                          <Handshake className="size-8 mx-auto mb-2 opacity-30" />
                          Nenhum contrato de representação comercial cadastrado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      contratosRepresentacaoFiltrados.map((cr) => {
                        const parceiroNome = cr.parceiro?.nome || "Veículo de Comunicação";
                        const parceiroCnpj = cr.parceiro?.cnpj || "—";
                        const isGatilho = cr.tipo_comissao === "gatilho_volume";

                        return (
                          <TableRow key={cr.id} className="text-xs">
                            <TableCell>
                              <div className="font-semibold text-sm text-foreground">
                                {cr.numero_contrato}
                              </div>
                              <div className="text-xs text-muted-foreground font-medium">
                                {parceiroNome}
                              </div>
                              <div className="text-[10px] text-muted-foreground font-mono">
                                CNPJ: {parceiroCnpj}
                              </div>
                            </TableCell>

                            <TableCell>
                              <div className="font-medium text-foreground">
                                {cr.territorio || "Distrito Federal e Entorno"}
                              </div>
                              <div className="text-[11px] text-muted-foreground">
                                {cr.produtos_representados && cr.produtos_representados.length > 0 ? (
                                  <Badge variant="secondary" className="text-[10px] mt-0.5">
                                    {cr.produtos_representados.length} formato(s)
                                  </Badge>
                                ) : (
                                  "Inventário Integral"
                                )}
                              </div>
                            </TableCell>

                            <TableCell>
                              <div className="flex flex-col gap-1 items-start">
                                {cr.permite_faturamento_centralizado_nexo && (
                                  <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-600 border-blue-500/20">
                                    Centralizado (Imp: {Number(cr.aliquota_imposto_nexo_percentual)}%)
                                  </Badge>
                                )}
                                {cr.permite_faturamento_direto_parceiro && (
                                  <Badge variant="outline" className="text-[10px] bg-purple-500/10 text-purple-600 border-purple-500/20">
                                    Direto Parceiro (Repasse {cr.prazo_repasse_dias || 3}d)
                                  </Badge>
                                )}
                              </div>
                            </TableCell>

                            <TableCell>
                              {isGatilho ? (
                                <div>
                                  <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/20">
                                    Gatilhos Escalonados
                                  </Badge>
                                  <div className="text-[10px] text-muted-foreground mt-0.5">
                                    {cr.regras_gatilho ? `${cr.regras_gatilho.length} faixas de faturamento` : "Progressivo"}
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-500/20 font-mono">
                                    {Number(cr.comissao_fixa_percentual)}% Fixa
                                  </Badge>
                                </div>
                              )}
                            </TableCell>

                            <TableCell>
                              <div className="space-y-0.5 text-[11px]">
                                {cr.garantia_comissao_pos_rescisao && (
                                  <div className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                                    <CheckCircle2 className="size-3" />
                                    <span>Pós-rescisão ativa</span>
                                  </div>
                                )}
                                {cr.comissao_sobre_renovacoes && (
                                  <div className="text-blue-600 dark:text-blue-400 flex items-center gap-1">
                                    <CheckCircle2 className="size-3" />
                                    <span>Renovações ativas</span>
                                  </div>
                                )}
                              </div>
                            </TableCell>

                            <TableCell>
                              <div className="font-medium text-foreground">
                                {cr.vigencia_meses || 12} meses
                              </div>
                              <div className="text-[10px] text-muted-foreground font-mono">
                                {cr.data_inicio || "—"} até {cr.data_fim || "—"}
                              </div>
                            </TableCell>

                            <TableCell>
                              <Badge
                                variant="outline"
                                className={`text-[10px] font-medium ${STATUS_CONTRATO_COLORS[cr.status] || ""}`}
                              >
                                {STATUS_CONTRATO_LABELS[cr.status] || cr.status}
                              </Badge>
                            </TableCell>

                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setViewMinutaRepresentacao(cr)}
                                  title="Visualizar Minuta Jurídica"
                                  className="h-8 w-8 p-0"
                                >
                                  <Eye className="size-4 text-primary" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setEditingContratoRepresentacao(cr);
                                    setModalRepresentacaoOpen(true);
                                  }}
                                  title="Editar Contrato"
                                  className="h-8 w-8 p-0"
                                >
                                  <Pencil className="size-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    if (confirm(`Deseja remover o contrato ${cr.numero_contrato}?`)) {
                                      deleteRepresentacaoMut.mutate(cr.id);
                                    }
                                  }}
                                  title="Excluir"
                                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="size-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ABA 2: PAINEL DE LIQUIDAÇÃO & REPASSES (SPLIT) */}
          <TabsContent value="liquidacao" className="space-y-4 pt-2">
            <PainelLiquidacaoRepasses tenantCnpj="68.279.031/0001-67" />
          </TabsContent>

          {/* ABA CONTRATOS */}
          <TabsContent value="contratos" className="space-y-4 pt-2">
            {/* Filtros */}
            <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
              <div className="relative w-full sm:w-80">
                <Search className="size-4 absolute left-3 top-3 text-muted-foreground" />
                <Input
                  placeholder="Buscar contrato, cliente ou parceiro..."
                  className="pl-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="flex gap-2 w-full sm:w-auto">
                <Select value={statusFiltro} onValueChange={setStatusFiltro}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os Status</SelectItem>
                    <SelectItem value="rascunho">Rascunho</SelectItem>
                    <SelectItem value="em_aprovacao">Em Aprovação</SelectItem>
                    <SelectItem value="aguardando_assinatura">Aguardando Assinatura</SelectItem>
                    <SelectItem value="assinado">Assinado</SelectItem>
                    <SelectItem value="cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tabela de Contratos */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Número / Título</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Cliente / Parceiro</TableHead>
                      <TableHead>Valor Total</TableHead>
                      <TableHead>Comissão / Repasse</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contratosFiltrados.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          Nenhum contrato encontrado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      contratosFiltrados.map((c) => {
                        const statusColors: Record<string, string> = {
                          assinado: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
                          aguardando_assinatura: "bg-amber-500/10 text-amber-600 border-amber-500/20",
                          em_aprovacao: "bg-blue-500/10 text-blue-600 border-blue-500/20",
                          rascunho: "bg-slate-500/10 text-slate-600 border-slate-500/20",
                          cancelado: "bg-rose-500/10 text-rose-600 border-rose-500/20",
                        };

                        return (
                          <TableRow key={c.id}>
                            <TableCell>
                              <div className="font-semibold text-sm">{c.numero}</div>
                              <div className="text-xs text-muted-foreground line-clamp-1">{c.titulo}</div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize text-xs">
                                {c.tipo}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm font-medium">
                                {c.cliente?.razao_social || c.parceiro?.razao_social || "—"}
                              </div>
                              {c.agencia && (
                                <div className="text-xs text-muted-foreground">
                                  Agência: {c.agencia.razao_social}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="font-semibold text-sm">
                              {formatBRL(c.valor)}
                            </TableCell>
                            <TableCell>
                              <div className="text-xs text-emerald-600 font-medium">
                                Com: {formatBRL(c.comissao_valor)} ({c.comissao_pct}%)
                              </div>
                              <div className="text-xs text-slate-500">
                                Rep: {formatBRL(c.repasse_valor)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={statusColors[c.status] || ""}>
                                {c.status.replace("_", " ").toUpperCase()}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right space-x-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setAssinaturaContrato(c)}
                                title="Gerenciar Assinaturas (Universal)"
                                className="h-8 px-2 text-xs text-primary border-primary/20 hover:bg-primary/10 gap-1"
                              >
                                <FileSignature className="size-3.5" />
                                Assinatura
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setViewContrato(c)}
                                title="Visualizar Contrato"
                              >
                                <Eye className="size-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => gerarPdfContrato(c, { nome: empresaNome })}
                                title="Baixar PDF do Contrato"
                              >
                                <Download className="size-4 text-primary" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  if (confirm("Deseja realmente excluir este contrato?")) {
                                    deleteMut.mutate(c.id);
                                  }
                                }}
                                title="Excluir"
                              >
                                <Trash2 className="size-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ABA MODELOS DE CONTRATOS */}
          <TabsContent value="modelos" className="space-y-4 pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {modelos.map((m) => (
                <Card key={m.id} className="flex flex-col justify-between">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="capitalize text-xs">
                        Modelo: {m.tipo}
                      </Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setModeloForm({
                            id: m.id,
                            titulo: m.titulo,
                            tipo: m.tipo,
                            conteudo: m.conteudo,
                          });
                          setModalModeloOpen(true);
                        }}
                      >
                        <Pencil className="size-4 mr-1" />
                        Editar
                      </Button>
                    </div>
                    <CardTitle className="text-base">{m.titulo}</CardTitle>
                    <CardDescription className="line-clamp-3 text-xs font-mono bg-muted/40 p-2 rounded">
                      {m.conteudo.slice(0, 200)}…
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-1">
                      {VARIAVEIS_CONTRATO.slice(0, 6).map((v) => (
                        <span key={v.key} className="bg-muted px-1.5 py-0.5 rounded">
                          {`{{${v.key}}}`}
                        </span>
                      ))}
                      <span>+ mais variáveis</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* DIALOG: NOVO / EDITAR CONTRATO */}
        <Dialog open={modalContratoOpen} onOpenChange={setModalContratoOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Elaborar Contrato Comercial</DialogTitle>
              <DialogDescription>
                Selecione o modelo e as partes para gerar o instrumento com dados integrados.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              <div className="space-y-2">
                <Label>Modelo de Contrato</Label>
                <Select
                  value={formData.modelo_id}
                  onValueChange={(v) => handleSelectModelo(v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um modelo base..." />
                  </SelectTrigger>
                  <SelectContent>
                    {modelos.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.titulo} ({m.tipo})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Título do Contrato</Label>
                <Input
                  value={formData.titulo}
                  onChange={(e) => setFormData((f) => ({ ...f, titulo: e.target.value }))}
                  placeholder="Ex: Contrato de Veiculação — Cliente X"
                />
              </div>

              <div className="space-y-2">
                <Label>Cliente / Anunciante</Label>
                <Select
                  value={formData.cliente_id}
                  onValueChange={(v) => setFormData((f) => ({ ...f, cliente_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o cliente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clientes.map((c: any) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.razao_social || c.nome_fantasia}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Veículo / Parceiro de Mídia (Quando aplicável)</Label>
                <Select
                  value={formData.parceiro_id}
                  onValueChange={(v) => setFormData((f) => ({ ...f, parceiro_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o parceiro..." />
                  </SelectTrigger>
                  <SelectContent>
                    {parceiros.map((p: any) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nome_fantasia || p.razao_social}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Valor Comercializado (R$)</Label>
                <Input
                  type="number"
                  value={formData.valor || ""}
                  onChange={(e) => {
                    const v = Number(e.target.value) || 0;
                    const cVal = v * (formData.comissao_pct / 100);
                    setFormData((f) => ({
                      ...f,
                      valor: v,
                      comissao_valor: cVal,
                      repasse_valor: v - cVal,
                    }));
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label>Comissão (%)</Label>
                <Input
                  type="number"
                  value={formData.comissao_pct || ""}
                  onChange={(e) => {
                    const pct = Number(e.target.value) || 0;
                    const cVal = formData.valor * (pct / 100);
                    setFormData((f) => ({
                      ...f,
                      comissao_pct: pct,
                      comissao_valor: cVal,
                      repasse_valor: formData.valor - cVal,
                    }));
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label>Data Início</Label>
                <Input
                  type="date"
                  value={formData.data_inicio}
                  onChange={(e) => setFormData((f) => ({ ...f, data_inicio: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Data Término</Label>
                <Input
                  type="date"
                  value={formData.data_fim}
                  onChange={(e) => setFormData((f) => ({ ...f, data_fim: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Texto Integral do Contrato (Variáveis preenchidas)</Label>
              <Textarea
                rows={10}
                className="font-mono text-xs"
                value={formData.conteudo_gerado}
                onChange={(e) => setFormData((f) => ({ ...f, conteudo_gerado: e.target.value }))}
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setModalContratoOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => saveContratoMut.mutate(formData)}
                disabled={saveContratoMut.isPending || !formData.titulo}
              >
                Salvar Contrato
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* DIALOG: MODELO DE CONTRATO */}
        <Dialog open={modalModeloOpen} onOpenChange={setModalModeloOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editor de Modelo de Minuta</DialogTitle>
              <DialogDescription>
                Crie um modelo padrão. Utilize as tags {"{{VARIAVEL}}"} para substituição automática.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-2">
              <div className="space-y-2">
                <Label>Título do Modelo</Label>
                <Input
                  value={modeloForm.titulo}
                  onChange={(e) => setModeloForm((f) => ({ ...f, titulo: e.target.value }))}
                  placeholder="Ex: Contrato Padrão Anunciante Mídia"
                />
              </div>

              <div className="space-y-2">
                <Label>Tipo de Instrumento</Label>
                <Select
                  value={modeloForm.tipo}
                  onValueChange={(v: any) => setModeloForm((f) => ({ ...f, tipo: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cliente">Cliente / Anunciante</SelectItem>
                    <SelectItem value="parceiro">Veículo / Parceiro</SelectItem>
                    <SelectItem value="agencia">Agência de Publicidade</SelectItem>
                    <SelectItem value="prestacao_servicos">Prestação de Serviços</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Variáveis Disponíveis (Clique para copiar):</Label>
                <div className="flex flex-wrap gap-1.5 p-2 bg-muted/40 rounded border">
                  {VARIAVEIS_CONTRATO.map((v) => (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => {
                        setModeloForm((f) => ({
                          ...f,
                          conteudo: f.conteudo + ` {{${v.key}}}`,
                        }));
                        toast.info(`Variável {{${v.key}}} inserida no texto.`);
                      }}
                      className="text-xs font-mono bg-background hover:bg-primary hover:text-primary-foreground border px-1.5 py-0.5 rounded transition"
                      title={v.label}
                    >
                      {`{{${v.key}}}`}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Texto do Contrato</Label>
                <Textarea
                  rows={12}
                  className="font-mono text-xs"
                  value={modeloForm.conteudo}
                  onChange={(e) => setModeloForm((f) => ({ ...f, conteudo: e.target.value }))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setModalModeloOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => saveModeloMut.mutate(modeloForm)}
                disabled={saveModeloMut.isPending || !modeloForm.titulo || !modeloForm.conteudo}
              >
                Salvar Modelo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* DIALOG: VISUALIZAR CONTRATO */}
        {viewContrato && (
          <Dialog open={!!viewContrato} onOpenChange={() => setViewContrato(null)}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <Badge variant="outline">{viewContrato.numero}</Badge>
                  <Button
                    size="sm"
                    onClick={() => gerarPdfContrato(viewContrato, { nome: empresaNome })}
                    className="gap-1.5"
                  >
                    <Download className="size-4" />
                    Exportar PDF
                  </Button>
                </div>
                <DialogTitle>{viewContrato.titulo}</DialogTitle>
                <DialogDescription>
                  Valor Total: {formatBRL(viewContrato.valor)} | Comissão: {formatBRL(viewContrato.comissao_valor)} | Repasse: {formatBRL(viewContrato.repasse_valor)}
                </DialogDescription>
              </DialogHeader>

              <div className="p-4 bg-muted/30 rounded border font-mono text-xs whitespace-pre-wrap leading-relaxed">
                {viewContrato.conteudo_gerado || viewContrato.observacoes || "Sem conteúdo textual registrado."}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setViewContrato(null)}>
                  Fechar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* MODAL: NOVO / EDITAR CONTRATO DE REPRESENTAÇÃO */}
        <ContratoRepresentacaoFormModal
          open={modalRepresentacaoOpen}
          onOpenChange={setModalRepresentacaoOpen}
          contrato={editingContratoRepresentacao}
          tenantCnpj="68.279.031/0001-67"
        />

        {/* MODAL: VISUALIZAR MINUTA DO CONTRATO DE REPRESENTAÇÃO */}
        {viewMinutaRepresentacao && (
          <Dialog
            open={!!viewMinutaRepresentacao}
            onOpenChange={() => setViewMinutaRepresentacao(null)}
          >
            <DialogContent className="max-w-4xl max-h-[88vh] flex flex-col">
              <DialogHeader>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono">
                      {viewMinutaRepresentacao.numero_contrato}
                    </Badge>
                    <Badge
                      variant="outline"
                      className={`text-xs ${STATUS_CONTRATO_COLORS[viewMinutaRepresentacao.status] || ""}`}
                    >
                      {STATUS_CONTRATO_LABELS[viewMinutaRepresentacao.status] || viewMinutaRepresentacao.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs gap-1.5"
                      onClick={() => {
                        if (viewMinutaRepresentacao.conteudo_contrato_markdown) {
                          navigator.clipboard.writeText(viewMinutaRepresentacao.conteudo_contrato_markdown);
                          toast.success("Minuta copiada para a área de transferência!");
                        }
                      }}
                    >
                      <Copy className="size-3.5" />
                      Copiar Markdown
                    </Button>
                    <Button
                      size="sm"
                      className="text-xs gap-1.5"
                      onClick={() => window.print()}
                    >
                      <Printer className="size-3.5" />
                      Imprimir / Salvar PDF
                    </Button>
                  </div>
                </div>
                <DialogTitle className="text-lg">
                  Contrato de Representação Comercial — {viewMinutaRepresentacao.parceiro?.nome || "Veículo"}
                </DialogTitle>
                <DialogDescription className="text-xs">
                  Instrumento com cláusulas bilaterais, modelo de faturamento, split e gatilhos de remuneração.
                </DialogDescription>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto p-4 bg-muted/20 rounded border border-border/60">
                <div className="font-sans text-xs whitespace-pre-wrap leading-relaxed text-foreground select-text">
                  {viewMinutaRepresentacao.conteudo_contrato_markdown || "Minuta contratual não processada."}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setViewMinutaRepresentacao(null)}>
                  Fechar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* MODAL UNIVERSAL DE ASSINATURAS */}
        {assinaturaContrato && (
          <UniversalAssinaturaModal
            open={!!assinaturaContrato}
            onOpenChange={(op) => {
              if (!op) setAssinaturaContrato(null);
            }}
            referenciaTipo="contratos"
            referenciaId={assinaturaContrato.id}
            documentoTipo="contrato"
            tituloPadrao={`Contrato: ${assinaturaContrato.titulo}`}
            numeroPadrao={assinaturaContrato.numero}
          />
        )}
      </div>
    </AppShell>
  );
}
